import { ref } from 'vue';
import { useLogout } from '@miele/user-management-frontend';
import { type MieleStoredGlobals } from '@/types/shared-types';
import { isAccountURL, isAuthedUrl } from '@/utils/isAuthedUrl';
import MieleAuthService, { type Instance } from '@/services/miele-auth-service';

export default function useAuth() {
    const authGlobalsConfig = ref<MieleStoredGlobals>();
    const authService = ref<Instance>();

    const checkAuthentication = async (): Promise<boolean> => {
        if (!authGlobalsConfig.value?.['general.experienceApi'] || !authService.value) return false;

        // Check both hybris and jwt sessions are still active
        const [hybrisResult, authResult] = await Promise.all([
            authService.value.checkHybrisSessionValid(),
            authService.value.checkAuthSessionValid(),
        ]);

        if (!hybrisResult || !authResult) {
            if (isAccountURL(window.location.pathname)) {
                window.location.assign('/');
            }

            if (authResult) {
                // when hybris session is invalid but auth session is valid (for eg. session timeout):
                // clear the auth session & jwt via experience-api
                await authService.value.logoutAuthSession();
            }

            return false;
        }

        return true;
    };

    const sessionSignOut = async (): Promise<boolean> => {
        if (!authService.value) return false;

        // TODO: use and check isLoggedIn when its fully synced from UMF
        const { logout } = useLogout();
        await logout();

        await authService.value.logoutAuthSession();

        // redirect if in any of the /e/ use pages
        if (isAuthedUrl(window.location.pathname)) {
            window.location.assign('/');
        } else {
            // make sure the page is reflects session
            // TODO: somehow ping hybris page to refresh page
            window.location.reload();
        }

        // if logout() fails, it will never reach here
        return true;
    };

    const setAuthConfig = (globalsConfig: MieleStoredGlobals) => {
        authGlobalsConfig.value = globalsConfig;
        authService.value = new MieleAuthService(globalsConfig);
    };

    return {
        setAuthConfig,
        checkAuthentication,
        sessionSignOut,
    };
}
